import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';
import { di } from '../../../../../state/inversify.config';
import {
  AssessmentRepository,
  PlayerRepository,
} from '../../../../../state/reduxRepositories';
import { AssessmentQueryParamService } from '../../assessment.queryParam.service';
import { AssessmentCreationViewService } from '../../assessmentCreationView/assessmentCreation.view.service';
import { TakeAssessmentDomain } from '../../takeAssessment/takeAssessmentDomainService';
import { version } from '../../../../../version';
import { getRouteByAccountType } from '../../../../../routes/getRouteByAccountType';
import { Assessment } from '../../../../../state/reduxRepositories/assessments/assessments.interfaces';
import { AssessmentDomainService } from '../../assessmentDomainService';
import { AuthService } from '../../../../../state/helperServices/auth/auth.service';
import { UserNotificationContext } from '../../../../../components/userNotification/userNotificationContext';
import { UserNotificationType } from '../../../../../components/userNotification/userNotificationType.enum';
import { Spinner } from '../../../../../components/spinner/spinner';
import { Button } from '../../../../../components/button/button';
import { ToggleQuestion } from '../../takeAssessment/components/questionSet/toggleQuestion.dumb';
import { useCustomTranslation } from '../../../../../translation/customTranslation';

export function ClearedToPlay() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // TODO : This should be handled in a better way
  const { t: customT } = useCustomTranslation();
  const { addUserNotification } = useContext(UserNotificationContext);

  // TODO: move into a domainService and only return accountType or even better find a way so that you don't have to send down the accountType
  const authService = di.get<AuthService>(AuthService);
  const loggedInUser = authService.useLoggedInUserSelector();

  const takeAssessmentDomain =
    di.get<TakeAssessmentDomain>(TakeAssessmentDomain);
  const assessmentInfo = takeAssessmentDomain.assessmentTypeSpecs();
  const assessmentDomainService = di.get<AssessmentDomainService>(
    AssessmentDomainService,
  );
  const assessments: Assessment[] =
    assessmentDomainService.useAssessmentResourceSelector();

  // TODO: move all services and logic into domain service
  const assessmentRepository =
    di.get<AssessmentRepository>(AssessmentRepository);
  const errors = assessmentRepository.useErrorsSelector();

  const assessmentQueryParamService = di.get<AssessmentQueryParamService>(
    AssessmentQueryParamService,
  );
  const assessmentCreationViewService = di.get<AssessmentCreationViewService>(
    AssessmentCreationViewService,
  );
  const module = assessmentCreationViewService.useModuleSelector();
  const queryParams = assessmentQueryParamService.useSelectMergedQuery();

  const playerRepository: PlayerRepository =
    di.get<PlayerRepository>(PlayerRepository);
  const player = playerRepository
    .useResourcesSelector()
    .find((p) => p.id === queryParams?.selectedPlayerId);
  const playerId: string | undefined = player?.id;

  const [clearedToPlay, setClearedToPlay] = useState('');
  const loading = useRef(false);
  const question = [
    {
      key: 'clearedToPlayStatus',
      label: customT('IS_PLAYER_CLEAR_TO_PLAY'),
    },
  ];

  const getNewestAssessment = (): Assessment | null => {
    if (assessments.length === 0) {
      return null;
    }

    return assessments.reduce((prev, current) => {
      const prevDate = new Date(prev.createdAt);
      const currentDate = new Date(current.createdAt);
      return prevDate > currentDate ? prev : current;
    });
  };

  const [prevNewestAssessment] = useState(getNewestAssessment());

  const createAssessment = (): void => {
    loading.current = true;
    assessmentRepository.dispatchCreate({
      assessment: {
        name: 'assessment',
        assessmentType: assessmentInfo.assessmentSpecifications.info.identifier,
        assessmentVersion: assessmentInfo.assessmentSpecifications.info.version,
        createdOnSoftwareVersion: version,
        info: assessmentInfo.assessmentSpecifications,
        assessment: module,
        clearedToPlay: clearedToPlay !== t('ID_GENERIC_NO'),
        pass: true,
      },
      playerId: queryParams?.selectedPlayerId,
      playerFullName: `${player?.firstName} ${player?.lastName}`,
      timeZoneOffset: new Date().getTimezoneOffset(),
    });
  };

  useEffect((): void => {
    const currNewestAssessment: Assessment | null = getNewestAssessment();
    let readyToNavigateNext: boolean = false;

    if (currNewestAssessment) {
      if (prevNewestAssessment) {
        const prevDate: Date = new Date(prevNewestAssessment.createdAt);
        const currentDate: Date = new Date(currNewestAssessment.createdAt);
        if (
          currentDate > prevDate &&
          currNewestAssessment.id &&
          currNewestAssessment.pdfS3FileKey &&
          currNewestAssessment.playerId === playerId
        ) {
          loading.current = false;
          readyToNavigateNext = true;
        }
      } else if (
        currNewestAssessment.id &&
        currNewestAssessment.pdfS3FileKey &&
        currNewestAssessment.playerId === playerId
      ) {
        loading.current = false;
        readyToNavigateNext = true;
      }

      if (!loading.current && readyToNavigateNext) {
        navigate(
          `/${getRouteByAccountType(
            loggedInUser?.accountType,
          )}/assessment-history?selectedAssessmentId=${
            currNewestAssessment.id
          }`,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessments]);

  useEffect(() => {
    if (errors.length) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('UNABLE_TO_SAVE_ASSESSMENT'),
        UserNotificationType.ERROR,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const getClearedToPlayStatus = (key: string, value: string | boolean) => {
    setClearedToPlay(value.toString());
  };

  return (
    <Stack direction="column" spacing={4}>
      <ToggleQuestion
        questionList={question}
        onAnswerSelect={getClearedToPlayStatus}
        data={{ clearedToPlayStatus: clearedToPlay }}
      />
      {loading.current ? (
        <Spinner open={loading.current} />
      ) : (
        <Button
          label={t('SAVE_ASSESSMENT')}
          color="secondary"
          onClick={createAssessment}
          variant="contained"
          fullWidth
          type="button"
        />
      )}
    </Stack>
  );
}
